import Fuse from 'fuse.js';
import { selectorFamily, useRecoilValue } from 'recoil';
import CustomerDeductions from 'config/AdjustmentTypeMap/CustomerDeduction';
import { AdjustmentType } from 'enums';
import { isDateInRange } from 'functions';
import { deductions, deductionsFilter, useSetRefreshState } from 'state';
import { PaymentDetail } from 'types';

const SearchKeys = CustomerDeductions.gridColumns.map(c => c.field ?? '');

export const filteredDeductions = selectorFamily<PaymentDetail[], string>({
    key: 'filteredDeductions',
    get: (accountId) =>
        async ({ get }) => {
            const filter = get(deductionsFilter);
            const {
                customerId,
                dateRange,
                searchTerm,
                sort,
                deductionType,
                dotBillbackType
            } = filter;
            
            const data = get(deductions({ accountId, customerId, dateRange }));
            
            const list = data.filter(({ adjustmentTypeId, invoiceDate, marBillTypeDescription }) =>
                isDateInRange(dateRange, invoiceDate)
                && (deductionType ? adjustmentTypeId === deductionType : true)
                && (dotBillbackType !== undefined && deductionType === AdjustmentType.DotBillback
                    ? marBillTypeDescription === dotBillbackType
                    : true)
            );

            const normalizedSearchTerm = searchTerm?.trim();
            if (normalizedSearchTerm) {
                const fuse = new Fuse(list, { keys: SearchKeys, threshold: 0.2, ignoreLocation: true });
                return fuse.search(normalizedSearchTerm).map(r => r.item).sort(sort.compare);
            }

            return list.sort(sort.compare);
        }
});

export const useFilteredDeductions = (accountId: string) => useRecoilValue(filteredDeductions(accountId));
export const useRefreshFilteredDeductions = (accountId: string) => {
    const { customerId, dateRange } = useRecoilValue(deductionsFilter);
    return useSetRefreshState(deductions({ accountId, customerId, dateRange }));
};

export const availableDotBillbackTypes = selectorFamily<string[], string>({
    key: 'availableDotBillbackTypes',
    get: (accountId) =>
        ({ get }) => {
            const { customerId, dateRange } = get(deductionsFilter);
            const data = get(deductions({ accountId, customerId, dateRange }));
            const typeSet = new Set<string>();

            data.forEach(({ adjustmentTypeId, invoiceDate, marBillTypeDescription }) => {
                if (
                    adjustmentTypeId === AdjustmentType.DotBillback
                    && marBillTypeDescription
                    && isDateInRange(dateRange, invoiceDate)
                ) {
                    typeSet.add(marBillTypeDescription);
                }
            });

            return Array.from(typeSet).sort((a, b) => a.localeCompare(b));
        }
});

export const useAvailableDotBillbackTypes = (accountId: string) =>
    useRecoilValue(availableDotBillbackTypes(accountId));
